import { useMemo } from 'react';
import { getStorePageBySlug } from './api/client';
import { DEMO_CONTENT_SLUG, isThemePreview } from './api/previewVisit';
import { readContentPage, type StoreContentPage } from './themes/binding';
import { CONTACT_SLUG, contentPageSlug, type StoreLanguage } from '@xshop/shared';
import type { PageType } from './themes/registry';
import type { StoreFacts } from './elements/render/storeFacts';
import { useStoreRead, type StoreRead } from './elements/render/useStoreRead';

/** What a page reached at /pages/<handle> says: the merchant's words, or why there are none. */
export type ContentFacts = NonNullable<StoreFacts['page']>;

/** What the page says when the shop has no page at that address, or could not be asked for it. */
type PageTroubles = { missing: ContentFacts; failed: ContentFacts };

/** Those words in each language a store is served in; a missing page offers the one way on, to the catalogue. */
const TROUBLES: Readonly<Record<StoreLanguage, PageTroubles>> = {
  en: {
    missing: { title: 'Page not found', body: '<p>The page you were looking for does not exist or has moved.</p><p><a href="/collections/all">Continue shopping</a></p>' },
    failed: { title: 'This page could not be loaded', body: '<p>Check your connection, then reload the page to try again.</p>' }
  },
  fr: {
    missing: { title: 'Page introuvable', body: '<p>La page que vous cherchez n’existe pas ou a été déplacée.</p><p><a href="/collections/all">Continuer vos achats</a></p>' },
    failed: { title: 'Cette page n’a pas pu être chargée', body: '<p>Vérifiez votre connexion, puis rechargez la page pour réessayer.</p>' }
  },
  ar: {
    missing: { title: 'الصفحة غير موجودة', body: '<p>الصفحة التي تبحث عنها غير موجودة أو تم نقلها.</p><p><a href="/collections/all">متابعة التسوق</a></p>' },
    failed: { title: 'تعذّر تحميل هذه الصفحة', body: '<p>تحقق من اتصالك، ثم أعد تحميل الصفحة للمحاولة مرة أخرى.</p>' }
  }
};

// Turns the read of a merchant's page into what its title and body say, including when it is not there.
function contentFacts(read: StoreRead<StoreContentPage | null>, page: PageType, troubles: PageTroubles): ContentFacts | undefined {
  // A contact page with no page of the merchant's own keeps its own heading, and its form still works.
  if (read.status === 'missing') return page === 'contact' ? undefined : troubles.missing;
  if (read.status === 'failed') return troubles.failed;
  if (read.status !== 'ready') return undefined;
  return { title: read.data?.title ?? '', body: read.data?.bodyHtml ?? '' };
}

// Names the merchant page an address reads: the contact page's own, the address's handle, or a preview's sample.
function slugFor(page: PageType): string | null {
  if (page === 'contact') return CONTACT_SLUG;
  if (page !== 'article') return null;
  // A preview names no page of its own, so it falls back to one the demo store actually carries.
  return contentPageSlug(window.location.pathname, window.location.search) ?? (isThemePreview() ? DEMO_CONTENT_SLUG : null);
}

// Reads the merchant's published page for this address, and nothing at all for an address naming none; a page that is
// not there says so in the store's language.
export function useContentPage(page: PageType, language: StoreLanguage | null = null): ContentFacts | undefined {
  const slug = slugFor(page);
  const read = useStoreRead(slug ? `page:${slug}` : null, async () => readContentPage(await getStorePageBySlug(slug ?? '')));
  // Held steady with the read, since the facts it lands in are wiring for the whole tree.
  return useMemo(() => contentFacts(read, page, TROUBLES[language ?? 'en']), [read, page, language]);
}
